import type { NormalizedDailyMetrics, WorkoutSession } from '@vitalai/health-models';
import { toDateKey } from '@vitalai/shared';
import {
  SLEEP_ANALYSIS_VALUE,
  type HealthKitSample,
  type RawHealthKitSleepSample,
  type RawWorkoutSample,
} from './healthKitTypes';

function isWorkoutSample(sample: HealthKitSample): sample is RawWorkoutSample {
  return sample.type === 'workout';
}

function isSleepSample(sample: HealthKitSample): sample is RawHealthKitSleepSample {
  return sample.type === 'sleepAnalysis';
}

function average(values: number[]): number {
  return values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;
}

function minutesBetween(startMillis: number, endMillis: number): number {
  return Math.max(0, (endMillis - startMillis) / 60000);
}

/** HKUnit.percent() representa SpO2 como fraccion (0-1); VitalAI lo muestra en escala 0-100. */
function toPercentScale(value: number): number {
  return value <= 1 ? value * 100 : value;
}

interface DayBucket {
  sleepSegments: RawHealthKitSleepSample[];
  heartRate: number[];
  restingHeartRate: number[];
  hrv: number[];
  spo2: number[];
  respiratoryRate: number[];
  steps: number;
  activeEnergyBurned: number;
  exerciseMinutes: number;
  bodyMassKg: number | null;
  bodyTemperature: number | null;
  workouts: RawWorkoutSample[];
}

function createEmptyBucket(): DayBucket {
  return {
    sleepSegments: [],
    heartRate: [],
    restingHeartRate: [],
    hrv: [],
    spo2: [],
    respiratoryRate: [],
    steps: 0,
    activeEnergyBurned: 0,
    exerciseMinutes: 0,
    bodyMassKg: null,
    bodyTemperature: null,
    workouts: [],
  };
}

function buildSleepMetrics(date: string, segments: RawHealthKitSleepSample[]): NormalizedDailyMetrics['sleep'] {
  if (segments.length === 0) {
    const fallback = new Date(`${date}T00:00:00.000Z`).toISOString();
    return {
      bedtime: fallback,
      sleepStart: fallback,
      wakeTime: fallback,
      totalMinutes: 0,
      awakeMinutes: 0,
      coreMinutes: 0,
      deepMinutes: 0,
      remMinutes: 0,
      efficiency: 0,
      latencyMinutes: 0,
      awakenings: 0,
    };
  }

  const sorted = [...segments].sort((a, b) => a.startDate - b.startDate);

  let coreMinutes = 0;
  let deepMinutes = 0;
  let remMinutes = 0;
  let awakeMinutes = 0;
  let inBedStart: number | null = null;
  let asleepStart: number | null = null;
  let sleepEnd: number | null = null;
  let awakeningsDuringSleep = 0;

  for (const segment of sorted) {
    const duration = minutesBetween(segment.startDate, segment.endDate);
    switch (segment.value) {
      case SLEEP_ANALYSIS_VALUE.inBed:
        inBedStart = inBedStart === null ? segment.startDate : Math.min(inBedStart, segment.startDate);
        break;
      case SLEEP_ANALYSIS_VALUE.asleepCore:
      case SLEEP_ANALYSIS_VALUE.asleepUnspecified:
        coreMinutes += duration;
        asleepStart = asleepStart === null ? segment.startDate : Math.min(asleepStart, segment.startDate);
        sleepEnd = sleepEnd === null ? segment.endDate : Math.max(sleepEnd, segment.endDate);
        break;
      case SLEEP_ANALYSIS_VALUE.asleepDeep:
        deepMinutes += duration;
        asleepStart = asleepStart === null ? segment.startDate : Math.min(asleepStart, segment.startDate);
        sleepEnd = sleepEnd === null ? segment.endDate : Math.max(sleepEnd, segment.endDate);
        break;
      case SLEEP_ANALYSIS_VALUE.asleepREM:
        remMinutes += duration;
        asleepStart = asleepStart === null ? segment.startDate : Math.min(asleepStart, segment.startDate);
        sleepEnd = sleepEnd === null ? segment.endDate : Math.max(sleepEnd, segment.endDate);
        break;
      case SLEEP_ANALYSIS_VALUE.awake:
        awakeMinutes += duration;
        if (asleepStart !== null && segment.startDate > asleepStart) {
          awakeningsDuringSleep += 1;
        }
        sleepEnd = sleepEnd === null ? segment.endDate : Math.max(sleepEnd, segment.endDate);
        break;
      default:
        break;
    }
  }

  const firstSegment = sorted[0]!;
  const lastSegment = sorted[sorted.length - 1]!;
  const totalMinutes = coreMinutes + deepMinutes + remMinutes;
  const bedtimeMillis = inBedStart ?? asleepStart ?? firstSegment.startDate;
  const sleepStartMillis = asleepStart ?? bedtimeMillis;
  const wakeMillis = sleepEnd ?? lastSegment.endDate;
  const latencyMinutes = minutesBetween(bedtimeMillis, sleepStartMillis);
  const inBedMinutes = totalMinutes + awakeMinutes;
  const efficiency = inBedMinutes > 0 ? Math.round((totalMinutes / inBedMinutes) * 100) : 0;

  return {
    bedtime: new Date(bedtimeMillis).toISOString(),
    sleepStart: new Date(sleepStartMillis).toISOString(),
    wakeTime: new Date(wakeMillis).toISOString(),
    totalMinutes: Math.round(totalMinutes),
    awakeMinutes: Math.round(awakeMinutes),
    coreMinutes: Math.round(coreMinutes),
    deepMinutes: Math.round(deepMinutes),
    remMinutes: Math.round(remMinutes),
    efficiency,
    latencyMinutes: Math.round(latencyMinutes),
    awakenings: awakeningsDuringSleep,
  };
}

function buildWorkoutSession(workout: RawWorkoutSample, index: number, date: string): WorkoutSession {
  return {
    id: `${date}-hk-workout-${index}`,
    type: `hk_activity_${workout.workoutActivityType}`,
    startTime: new Date(workout.startDate).toISOString(),
    endTime: new Date(workout.endDate).toISOString(),
    durationMinutes: Math.round(workout.duration / 60),
    activeCalories: Math.round(workout.totalEnergyBurned),
  };
}

function buildNormalizedDay(date: string, bucket: DayBucket): NormalizedDailyMetrics {
  return {
    date,
    source: 'healthkit',
    sleep: buildSleepMetrics(date, bucket.sleepSegments),
    recovery: {
      hrv: Math.round(average(bucket.hrv)),
      restingHeartRate: Math.round(average(bucket.restingHeartRate)),
      averageHeartRate: Math.round(average(bucket.heartRate)),
      minHeartRate: bucket.heartRate.length > 0 ? Math.round(Math.min(...bucket.heartRate)) : 0,
      spo2: bucket.spo2.length > 0 ? Math.round(toPercentScale(average(bucket.spo2)) * 10) / 10 : 0,
      respiratoryRate: Math.round(average(bucket.respiratoryRate) * 10) / 10,
      // HealthKit no expone una "desviacion de temperatura" directa; se deja en 0
      // hasta que exista suficiente baseline personal para calcularla (Sprint 3+).
      temperatureDeviation: 0,
    },
    activity: {
      steps: Math.round(bucket.steps),
      activeCalories: Math.round(bucket.activeEnergyBurned),
      exerciseMinutes: Math.round(bucket.exerciseMinutes),
      distanceKm: 0,
      workouts: bucket.workouts.map((workout, index) => buildWorkoutSession(workout, index, date)),
    },
    // HealthKit no es una fuente de nutricion: estos campos los completa el
    // modulo de nutricion (registro manual / foto con IA, Sprint 5).
    nutrition: { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sodium: 0, sugar: 0, meals: [] },
    body: {
      weightKg: bucket.bodyMassKg ?? undefined,
      bodyTemperature: bucket.bodyTemperature ?? undefined,
    },
    tags: [],
  };
}

/**
 * Agrupa muestras crudas de HealthKit en NormalizedDailyMetrics, una por
 * dia. Cada muestra se atribuye al dia de su `endDate` (una noche de sueno o
 * un entrenamiento pertenecen al dia en que terminan).
 *
 * Simplificacion conocida: la latencia y los despertares se derivan
 * directamente de los segmentos inBed/awake/asleep* que entrega HealthKit,
 * sin heuristicas adicionales. Es suficiente para /health-debug; puede
 * refinarse sin cambiar el resto del sistema, que solo consume
 * NormalizedDailyMetrics.
 */
export function normalizeHealthKitSamples(samples: HealthKitSample[]): NormalizedDailyMetrics[] {
  const buckets = new Map<string, DayBucket>();

  function bucketFor(dateKey: string): DayBucket {
    let bucket = buckets.get(dateKey);
    if (!bucket) {
      bucket = createEmptyBucket();
      buckets.set(dateKey, bucket);
    }
    return bucket;
  }

  for (const sample of samples) {
    const dateKey = toDateKey(new Date(sample.endDate));
    const bucket = bucketFor(dateKey);

    if (isWorkoutSample(sample)) {
      bucket.workouts.push(sample);
      continue;
    }
    if (isSleepSample(sample)) {
      bucket.sleepSegments.push(sample);
      continue;
    }

    switch (sample.type) {
      case 'heartRate':
        bucket.heartRate.push(sample.value);
        break;
      case 'restingHeartRate':
        bucket.restingHeartRate.push(sample.value);
        break;
      case 'hrv':
        bucket.hrv.push(sample.value);
        break;
      case 'spo2':
        bucket.spo2.push(sample.value);
        break;
      case 'respiratoryRate':
        bucket.respiratoryRate.push(sample.value);
        break;
      case 'steps':
        bucket.steps += sample.value;
        break;
      case 'activeEnergyBurned':
        bucket.activeEnergyBurned += sample.value;
        break;
      case 'exerciseTime':
        bucket.exerciseMinutes += sample.value;
        break;
      case 'bodyMass':
        bucket.bodyMassKg = sample.value;
        break;
      case 'bodyTemperature':
        bucket.bodyTemperature = sample.value;
        break;
    }
  }

  return Array.from(buckets.entries())
    .sort(([dateA], [dateB]) => (dateA < dateB ? -1 : 1))
    .map(([date, bucket]) => buildNormalizedDay(date, bucket));
}
